import React, { useState, useEffect } from 'react';
import SuperAdminSidebar from '@/components/sidebar/superAdminSidebar';
import { SuperAdminService } from '@/services/superadmin.service';
import { toast, Toaster } from 'react-hot-toast';
import { MessageSquare, UserCheck, Check, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import logoImg from '@/assets/Sthitih_Logo.png';
import { SuperAdminMobileHeader } from '@/components/navigation/SuperAdminMobileHeader';

interface Ticket {
  id: string;
  client_id: string;
  subject: string;
  description: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high';
  assigned_agent: string | null;
  created_at: string;
  Client?: { name: string; subdomain: string };
}

const statusColors: Record<string, string> = {
  open: 'bg-[#FFF3D8] text-[#C47C14] border-[#C47C14]/20',
  in_progress: 'bg-[#E7F4F0] text-[#2E7D68] border-[#2E7D68]/20',
  resolved: 'bg-[#DFF5E7] text-[#1F7A4F] border-[#1F7A4F]/20',
  closed: 'bg-[#EFE5D6] text-[#8A4B22] border-[#8A4B22]/20',
};

const priorityColors: Record<string, string> = {
  low: 'bg-[#F7F2EA] text-[#6F706C] border-[#DDD0BE]',
  medium: 'bg-[#FFF3D8] text-[#C47C14] border-[#C47C14]/20',
  high: 'bg-[#FCE8E8] text-[#D64545] border-[#D64545]/20',
};

const Support: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Ticket | null>(null);
  const [modalType, setModalType] = useState<'status' | 'assign' | null>(null);
  const [newStatus, setNewStatus] = useState<Ticket['status']>('open');
  const [agentName, setAgentName] = useState('');
  const [resultModal, setResultModal] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const data = await SuperAdminService.getTickets();
      setTickets(data);
    } catch {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTickets(); }, []);

  const openModal = (ticket: Ticket, type: 'status' | 'assign') => {
    setSelected(ticket);
    setNewStatus(ticket.status);
    setAgentName(ticket.assigned_agent || '');
    setModalType(type);
  };

  const closeModal = () => { setSelected(null); setModalType(null); };

  const handleSubmit = async () => {
    if (!selected) return;
    try {
      const payload: any = {};
      if (modalType === 'status') payload.status = newStatus;
      if (modalType === 'assign') payload.assigned_agent = agentName || null;
      await SuperAdminService.updateTicket(selected.id, payload);
      setResultModal({ type: 'success', message: modalType === 'status' ? 'Ticket status updated' : 'Agent assigned successfully' });
      closeModal();
      fetchTickets();
    } catch {
      setResultModal({ type: 'error', message: 'Failed to update ticket' });
      closeModal();
    }
  };

  const filtered = filterStatus === 'all' ? tickets : tickets.filter(t => t.status === filterStatus);

  return (
    <div className="flex min-h-screen bg-[#F7F2EA] text-[#243238]">
      <Toaster position="top-right" />
      <SuperAdminSidebar />

      <div className="flex-1 flex flex-col pl-0 pb-24 md:pl-20 md:pb-8 min-h-screen min-w-0 w-full relative">
        <SuperAdminMobileHeader title="Support Center" />

        <header className="hidden md:flex h-16 px-8 bg-[#F7F2EA] border-b border-[#DDD0BE] shadow-xs items-center justify-between sticky top-0 z-20 w-full">
          <h1 className="text-sm sm:text-base font-extrabold tracking-tight text-[#243238] uppercase">Support Center</h1>
          <img src={logoImg} alt="Sthitih Logo" className="h-10 w-auto object-contain rounded-md shadow-xs border border-black/5" />
        </header>

        <main className="flex-grow p-4 sm:p-8">
          {/* Top Actions & Filters Row */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div className="flex flex-wrap gap-2">
              {['all', 'open', 'in_progress', 'resolved', 'closed'].map(s => (
                <button 
                  key={s} 
                  onClick={() => setFilterStatus(s)}
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all ${filterStatus === s ? 'bg-[#1F5A43] text-white shadow-xs' : 'bg-[#F3ECE3] text-[#5A4634] hover:bg-[#E6DCD0] border border-[#D9CDBD]'}`}
                >
                  {s === 'all' ? 'All Tickets' : s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </button>
              ))}
            </div>
            <span className="text-[10px] font-bold text-[#8D8A83] uppercase tracking-wider">Operational Queue</span>
          </div>

          {/* Cards list layout instead of tables */}
          {loading ? (
            <div className="flex items-center justify-center h-40"><div className="w-8 h-8 border-4 border-[#1F5A43] border-t-transparent rounded-full animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-[#6F706C] text-xs font-semibold">No support tickets found matching the status filter.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((t) => (
                <div 
                  key={t.id} 
                  className="rounded-[18px] border border-[#E4D8C8] p-6 bg-[#FFFDF9] shadow-earth hover:shadow-earth-lg transition-all duration-200 flex flex-col justify-between"
                >
                  <div>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <span className="text-[10px] font-mono text-[#8D8A83] uppercase tracking-wider">Ticket #{t.id.slice(0, 8)}</span>
                        <h3 className="font-bold text-sm text-[#243238] mt-1">{t.subject}</h3>
                        <p className="text-[10px] text-[#6F706C] font-semibold mt-0.5">{t.Client?.name || 'Unknown Client'}</p>
                      </div>
                      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${statusColors[t.status]}`}>
                        {t.status.toUpperCase().replace('_', ' ')}
                      </span>
                    </div>

                    <p className="text-xs text-[#6F706C] line-clamp-3 bg-[#F7F2EA] p-3 rounded-xl border border-[#DDD0BE]/60 my-4 leading-relaxed">
                      {t.description}
                    </p>

                    <div className="space-y-2 mt-4 pt-3 border-t border-[#E4D8C8]">
                      <div className="flex justify-between text-xs text-[#4A5558]">
                        <span>Priority Level</span>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${priorityColors[t.priority]}`}>
                          {t.priority.toUpperCase()}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs text-[#4A5558]">
                        <span>Assigned Agent</span>
                        <span className="font-bold text-[#243238]">{t.assigned_agent || 'Unassigned'}</span>
                      </div>
                      <div className="flex justify-between text-xs text-[#4A5558]">
                        <span>Reported Date</span>
                        <span className="font-semibold text-[#6F706C]">{new Date(t.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Operational actions logically aligned at bottom */}
                  <div className="flex gap-2 mt-6 pt-3 border-t border-[#E4D8C8]">
                    <button 
                      onClick={() => openModal(t, 'status')} 
                      className="flex-1 btn-primary text-xs flex items-center justify-center gap-1.5"
                    >
                      <MessageSquare className="w-4 h-4" /> Status
                    </button>
                    <button 
                      onClick={() => openModal(t, 'assign')} 
                      className="flex-1 btn-secondary text-xs flex items-center justify-center gap-1.5"
                    >
                      <UserCheck className="w-4 h-4" /> Assign
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Action Modal */}
      <AnimatePresence>
        {modalType && selected && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000]" onClick={closeModal}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#FFFDF9] rounded-[18px] border border-[#E4D8C8] shadow-earth p-8 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
              <h2 className="text-sm font-bold text-[#243238] uppercase tracking-wider mb-1">{modalType === 'status' ? 'Update Ticket Status' : 'Assign Support Agent'}</h2>
              <p className="text-xs text-[#6F706C] mb-6">Updating details for: <strong className="text-[#243238]">{selected.subject}</strong></p>
              {modalType === 'status' && (
                <div className="space-y-3">
                  {(['open', 'in_progress', 'resolved', 'closed'] as const).map(s => (
                    <label key={s} className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${newStatus === s ? 'border-[#1F5A43] bg-[#DFF5E7]/40' : 'border-[#E4D8C8] hover:border-[#DDD0BE]'}`}>
                      <input type="radio" name="ticketStatus" value={s} checked={newStatus === s} onChange={() => setNewStatus(s)} className="accent-[#1F5A43]" />
                      <span className="font-semibold text-xs text-[#243238] capitalize">{s.replace('_', ' ')}</span>
                    </label>
                  ))}
                </div>
              )}
              {modalType === 'assign' && (
                <div>
                  <label className="block text-xs font-semibold text-[#4A5558] mb-2">Agent Name</label>
                  <input type="text" value={agentName} onChange={e => setAgentName(e.target.value)} placeholder="e.g. John Doe"
                    className="w-full px-4 py-3 border border-[#DCCFBE] rounded-xl focus:ring-2 focus:ring-[#1F5A43] outline-none text-xs text-[#243238] bg-white font-semibold" />
                </div>
              )}
              <div className="flex gap-3 mt-8">
                <button onClick={closeModal} className="flex-1 btn-secondary text-xs">Cancel</button>
                <button onClick={handleSubmit} className="flex-1 btn-primary text-xs">Confirm</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Result Confirmation Box */}
      <AnimatePresence>
        {resultModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1001]" onClick={() => setResultModal(null)}>
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#FFFDF9] rounded-[18px] border border-[#E4D8C8] shadow-earth p-8 max-w-sm w-full mx-4 text-center" onClick={e => e.stopPropagation()}>
              <div className={`w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4 ${resultModal.type === 'success' ? 'bg-[#DFF5E7] text-[#1F7A4F]' : 'bg-[#FCE8E8] text-[#D64545]'}`}>
                {resultModal.type === 'success' ? <Check className="w-6 h-6" /> : <X className="w-6 h-6" />}
              </div>
              <h3 className="text-sm font-bold text-[#243238] uppercase tracking-wider mb-2">{resultModal.type === 'success' ? 'Success' : 'Error'}</h3>
              <p className="text-xs text-[#6F706C] mb-6">{resultModal.message}</p>
              <button onClick={() => setResultModal(null)} className="btn-primary text-xs px-6 py-2.5">Close</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Support;
